import type { CSSProperties } from "react";

// Solid, high-contrast hues for team avatars. Used to derive a stable color
// for teams that have not picked a custom one, so every team is still
// visually distinct out of the box.
const teamSolidPalette = [
  "#dc2626", // red
  "#ea580c", // orange
  "#d97706", // amber
  "#16a34a", // green
  "#0d9488", // teal
  "#2563eb", // blue
  "#4f46e5", // indigo
  "#9333ea", // purple
  "#c026d3", // fuchsia
  "#db2777", // pink
] as const;

// Matches the backend validator: a full `#RRGGBB` hex string.
export const isValidHexColor = (value: string): boolean => /^#[0-9a-fA-F]{6}$/.test(value);

const hashString = (value: string): number => {
  let hash = 0;

  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) >>> 0;
  }

  return hash;
};

/**
 * Pick a legible text color (near-black or white) for a solid `#RRGGBB`
 * background using the WCAG relative-luminance formula.
 */
export const contrastTextColor = (hex: string): string => {
  const normalized = hex.replace("#", "");
  if (normalized.length !== 6) {
    return "#ffffff";
  }

  const channels = [0, 2, 4].map((offset) => {
    const value = parseInt(normalized.slice(offset, offset + 2), 16) / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });
  const luminance = 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];

  return luminance > 0.5 ? "#1f2937" : "#ffffff";
};

const stableTeamColor = (seed: string | number | bigint | null | undefined): string => {
  const normalizedSeed = String(seed ?? "unknown");
  return teamSolidPalette[hashString(normalizedSeed) % teamSolidPalette.length];
};

/**
 * Avatar style for a team: the custom color when set, otherwise a stable color
 * derived from the team id. Text color is chosen for contrast either way.
 */
export const getTeamAvatarStyle = (team: {
  id?: string | number | bigint | null;
  color?: string | null;
}): CSSProperties => {
  const background = team.color ?? stableTeamColor(team.id);
  return {
    backgroundColor: background,
    color: contrastTextColor(background),
  };
};
